import { ChangeEvent, useEffect, useRef, useState } from 'react';

import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { nanoid } from 'nanoid';
import { Path, useForm } from 'react-hook-form';
import { useReactToPrint } from 'react-to-print';
import { z } from 'zod';

import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormFieldItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useUserStore } from '@/stores/user.store';

import { QRPrintDocument } from './qr-print-document';

const formSchema = z.object({
  qrCodeCount: z.coerce.number({ message: 'Must be a valid number' }).min(1, { message: 'Must be at least 1' }).max(499, { message: 'Must be at most 499' }),
});

type FormInput = z.input<typeof formSchema>;
type FormOutput = z.output<typeof formSchema>;

export function QRBlankDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { org } = useUserStore();
  const [toPrintQRCodes, setToPrintQRCodes] = useState<{ label?: string; qr: string }[]>([]);
  const [isBlankQRCodesGenerating, setIsBlankQRCodesGenerating] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handlePrintBlankQrCodes = useReactToPrint({
    documentTitle: 'Gearbelt Blank QR Codes',
    contentRef,
    onAfterPrint: () => {
      setIsBlankQRCodesGenerating(false);
      setToPrintQRCodes([]);
    },
  });

  useEffect(() => {
    if (toPrintQRCodes.length) {
      handlePrintBlankQrCodes();
    }
  }, [toPrintQRCodes]);

  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      qrCodeCount: 9,
    },
  });

  function handleSubmission(data: FormOutput) {
    setIsBlankQRCodesGenerating(true);
    setToPrintQRCodes(
      Array.from({ length: data.qrCodeCount }, (_, i) => {
        return { qr: `${nanoid(10)}?ws=${org?.workspaceId}` };
      }),
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex h-fit w-[510px] flex-col gap-3">
        <DialogHeader>
          <DialogTitle>Blank QR codes</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form className="" onSubmit={form.handleSubmit(handleSubmission)}>
            <div className="flex flex-col gap-4">
              <div className="text-default-text text-sm">
                Get a PDF containing blank QR codes which can be assigned to assets later. This option is useful if you want to print several sheets of stickers
                now so they are ready when you acquire new assets in future.
              </div>
              <div className="flex flex-row justify-between gap-4">
                <div>
                  <div className="text-default-text text-sm">How many blank QR codes do you need?</div>
                  <div className="text-secondary-text text-xs">(there are 9 QR codes per page)</div>
                </div>
                <div>
                  <FormFieldItem<FormInput, Path<FormInput>> control={form.control} name="qrCodeCount" labelSize="small">
                    {({ value, onChange, error }) => (
                      <Input
                        placeholder={'Enter number ...'}
                        value={value as number}
                        onChange={onChange as unknown as (e: ChangeEvent<HTMLInputElement>) => void}
                        error={!!error}
                      />
                    )}
                  </FormFieldItem>
                </div>
              </div>
              <div>
                <Button variant="success" type="submit" disabled={isBlankQRCodesGenerating}>
                  Generate
                </Button>
              </div>
            </div>
          </form>
        </Form>
        <DialogFooter>
          <QRPrintDocument contentRef={contentRef} qrs={toPrintQRCodes} type="assets" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
